import {testIdProps} from '@github-ui/test-id-props'
import {Dialog} from '@primer/react/experimental'
import {type RefObject, useCallback} from 'react'

import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {useDeleteSavedViewMutation} from '../../hooks/use-delete-saved-view-mutation'
import styles from './SavedViewDialog.module.css'
import {SavedViewMutationErrorBanner} from './SavedViewMutationErrorBanner'
import type {SavedView} from './types'

interface DeleteSavedViewDialogProps {
  view: SavedView
  owner: string
  repo: string
  onClose: () => void
  onSuccess?: () => void
  returnFocusRef?: RefObject<HTMLElement | null>
}

export function DeleteSavedViewDialog({
  view,
  owner,
  repo,
  onClose,
  onSuccess,
  returnFocusRef,
}: DeleteSavedViewDialogProps) {
  const {mutate: deleteView, isPending, error: deleteError} = useDeleteSavedViewMutation({owner, repo})

  const handleSubmit = useCallback(() => {
    if (isPending) return
    deleteView(
      {id: view.id},
      {
        onSuccess: () => {
          onSuccess?.()
          onClose()
        },
      },
    )
  }, [deleteView, isPending, onClose, onSuccess, view.id])

  return (
    <Dialog
      title={LABELS.views.deleteTitle}
      onClose={onClose}
      role="alertdialog"
      width="medium"
      height="auto"
      returnFocusRef={returnFocusRef}
      renderBody={({children}) => <div className={styles.body}>{children}</div>}
      footerButtons={[
        {buttonType: 'default', content: BUTTON_LABELS.cancel, onClick: onClose},
        {
          buttonType: 'danger',
          content: LABELS.views.deleteConfirmationButton,
          onClick: handleSubmit,
          loading: isPending,
        },
      ]}
    >
      <div {...testIdProps('delete-saved-view-dialog')}>
        <SavedViewMutationErrorBanner
          message={deleteError ? LABELS.views.deleteError : null}
          onRetry={handleSubmit}
          isRetrying={isPending}
          flush
        />
        <div className={styles.content}>
          <p>{LABELS.views.deleteContent(view.name)}</p>
        </div>
      </div>
    </Dialog>
  )
}
